import type { BatchStatus } from '../api/getBatch';
import getPerformanceCaptureStatus, {
  CAPTURE_SOURCE_WAIT_TIMEOUT_MS,
  type PerformanceCaptureRig,
} from '../api/getPerformanceCaptureStatus';
import { BaseStore } from './BaseStore';

export type JobNotification = {
  batchID: string;
  event: 'started' | 'finished' | 'failed';
  title: string;
  createdAt: string;
  status: 'new' | 'read';
  requiredRig?: PerformanceCaptureRig;
  sourceWaitStartedAt?: number;
};

type State = { userID: string | null; notifications: JobNotification[] };
type Deps = {
  storage: () => Pick<Storage, 'getItem' | 'setItem'> | undefined;
  getBatchStatus: (
    batchID: string,
    apiKey: string,
    signal: AbortSignal,
    requiredRig?: PerformanceCaptureRig,
  ) => Promise<BatchStatus | 'AWAITING_SOURCES' | undefined>;
};
const initialState: State = { userID: null, notifications: [] };
const MAX_NOTIFICATIONS = 100;
const STATUS_POLL_INTERVAL = 15_000;
const FAILED_STATUSES = new Set<BatchStatus>([
  'FAILED',
  'REJECTED',
  'CANCELLED',
]);
const storageKey = (userID: string) => `cartwheel:job-notifications:${userID}`;

export class JobNotificationStore extends BaseStore<State, Deps> {
  private apiKey: string | undefined;
  private statusTimer: ReturnType<typeof setTimeout> | undefined;
  private statusRequest: AbortController | undefined;

  private handleVisibilityChange = () => {
    if (document.visibilityState === 'visible')
      void this.reconcilePendingJobs();
  };

  constructor(deps: Deps) {
    super({ ...initialState, notifications: [] }, deps);
  }

  reset() {
    this.stopMonitoring();
    this.state.userID = null;
    this.state.notifications = [];
  }

  destroy() {
    this.stopMonitoring();
    super.destroy();
  }

  setUser(userID: string | undefined) {
    if (this.state.userID === (userID ?? null)) return;
    this.reset();
    this.state.userID = userID ?? null;
    if (!userID) return;
    try {
      const stored: unknown = JSON.parse(
        this.deps.storage()?.getItem(storageKey(userID)) ?? '[]',
      );
      if (Array.isArray(stored))
        this.state.notifications = stored
          .flatMap(parseStoredNotification)
          .slice(0, MAX_NOTIFICATIONS);
    } catch {
      // Unavailable/corrupt browser storage must not interrupt a job.
    }
  }

  /** Credentials stay in memory; restored jobs resume checking on login. */
  startMonitoring(apiKey: string | undefined) {
    if (this.apiKey !== apiKey) {
      this.stopMonitoring();
      this.apiKey = apiKey;
    }
    if (apiKey && typeof document !== 'undefined')
      document.addEventListener(
        'visibilitychange',
        this.handleVisibilityChange,
      );
    return this.reconcilePendingJobs();
  }

  add(
    userID: string | undefined,
    input: Omit<JobNotification, 'createdAt' | 'status'>,
  ) {
    if (!userID) return;
    this.setUser(userID);
    const index = this.state.notifications.findIndex(
      (item) => item.batchID === input.batchID,
    );
    const existing = this.state.notifications[index];
    // Retries must not reopen a read event or regress a terminal job.
    if (
      existing &&
      (existing.event === input.event || existing.event !== 'started')
    )
      return;
    if (existing) this.state.notifications.splice(index, 1);
    this.state.notifications.unshift({
      ...input,
      createdAt: new Date().toISOString(),
      status: 'new',
    });
    this.state.notifications.splice(MAX_NOTIFICATIONS);
    this.persist();
    if (input.event === 'started') void this.reconcilePendingJobs();
  }

  markRead(batchID: string, event?: JobNotification['event']) {
    const notification = this.state.notifications.find(
      (item) => item.batchID === batchID,
    );
    if (
      !notification ||
      notification.status === 'read' ||
      (event && notification.event !== event)
    )
      return;
    notification.status = 'read';
    this.persist();
  }

  private stopMonitoring() {
    if (typeof document !== 'undefined')
      document.removeEventListener(
        'visibilitychange',
        this.handleVisibilityChange,
      );
    clearTimeout(this.statusTimer);
    this.statusTimer = undefined;
    this.statusRequest?.abort();
    this.statusRequest = undefined;
    this.apiKey = undefined;
  }

  private async reconcilePendingJobs() {
    if (this.statusRequest) return;
    clearTimeout(this.statusTimer);
    this.statusTimer = undefined;
    const { userID } = this.state;
    const apiKey = this.apiKey;
    const getBatchStatus = this.deps.getBatchStatus;
    if (!userID || !apiKey) return;
    const pending = this.state.notifications.filter(
      (item) => item.event === 'started',
    );
    if (!pending.length) return;
    const controller = new AbortController();
    this.statusRequest = controller;
    try {
      // A failed status request is retried without blocking the other jobs.
      await Promise.allSettled(
        pending.map(async (item) => {
          let status = await getBatchStatus(
            item.batchID,
            apiKey,
            controller.signal,
            item.requiredRig,
          );
          if (controller.signal.aborted || this.state.userID !== userID) return;
          if (status === 'AWAITING_SOURCES') {
            if (item.sourceWaitStartedAt === undefined) {
              item.sourceWaitStartedAt = Date.now();
              this.persist();
            }
            if (
              Date.now() - item.sourceWaitStartedAt <
              CAPTURE_SOURCE_WAIT_TIMEOUT_MS
            )
              return;
            status = 'FAILED';
          } else if (status && item.sourceWaitStartedAt !== undefined) {
            delete item.sourceWaitStartedAt;
            this.persist();
          }
          if (
            status !== 'COMPLETED' &&
            (!status || !FAILED_STATUSES.has(status))
          )
            return;
          this.add(userID, {
            batchID: item.batchID,
            title: item.title,
            event: status === 'COMPLETED' ? 'finished' : 'failed',
          });
        }),
      );
    } finally {
      if (this.statusRequest === controller) {
        this.statusRequest = undefined;
        if (this.state.notifications.some((item) => item.event === 'started')) {
          this.statusTimer = setTimeout(() => {
            void this.reconcilePendingJobs();
          }, STATUS_POLL_INTERVAL);
        }
      }
    }
  }

  private persist() {
    if (!this.state.userID) return;
    try {
      this.deps
        .storage()
        ?.setItem(
          storageKey(this.state.userID),
          JSON.stringify(this.state.notifications),
        );
    } catch {
      // Notifications still work in memory if browser storage is full.
    }
  }
}

function parseStoredNotification(value: unknown): JobNotification[] {
  if (!value || typeof value !== 'object') return [];
  const item = value as Partial<JobNotification> & { jobID?: string };
  // Existing browser entries used jobID for the batch identifier.
  const batchID = item.batchID ?? item.jobID;
  if (
    typeof batchID !== 'string' ||
    typeof item.title !== 'string' ||
    !['started', 'finished', 'failed'].includes(item.event ?? '') ||
    typeof item.createdAt !== 'string' ||
    !Number.isFinite(Date.parse(item.createdAt)) ||
    (item.status !== 'new' && item.status !== 'read')
  )
    return [];
  return [
    {
      batchID,
      createdAt: item.createdAt,
      event: item.event!,
      status: item.status,
      title: item.title,
      ...(item.requiredRig === 'axel' || item.requiredRig === 'mhr'
        ? { requiredRig: item.requiredRig }
        : {}),
      ...(typeof item.sourceWaitStartedAt === 'number' &&
      Number.isFinite(item.sourceWaitStartedAt)
        ? { sourceWaitStartedAt: item.sourceWaitStartedAt }
        : {}),
    },
  ];
}

export const jobNotificationStore = new JobNotificationStore({
  getBatchStatus: async (batchID, apiKey, signal, requiredRig) => {
    const result = await getPerformanceCaptureStatus(
      batchID,
      apiKey,
      signal,
      requiredRig,
    );
    return result.state === 'ready'
      ? 'COMPLETED'
      : result.state === 'failed'
        ? 'FAILED'
        : result.terminal
          ? 'AWAITING_SOURCES'
          : 'IN_PROGRESS';
  },
  storage: () =>
    typeof localStorage === 'undefined' ? undefined : localStorage,
});
